import { Trip } from "@/types/trip";
import { TimelineEntry } from "./TimelineEntry";
import { TimelineDateIndicator } from "./TimelineDateIndicator";
import { useIsMobile } from "@/hooks/use-mobile";
import { parseISO, isAfter, isBefore, compareAsc } from "date-fns";
import { DesktopTripList } from "./DesktopTripList";
import { useAnalytics } from "@/hooks/use-analytics";
import { getAdSenseConfig } from '@/constants/adsense';
import { useFeatureFlagStatus } from "@/utils/FeatureFlagProvider";
import { useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useSpaSafeShowAd } from "@/hooks/useSpaSafeShowAd";
import AdSenseWrapper from "../ads/AdSenseWrapper";
import React from "react";
import { GoogleAdsScriptLoader } from "../ads/GoogleAdsScriptLoader";


interface TripListProps {
  futureTrips: Trip[];
  pastTrips: Trip[];
  isTimelineVisible: boolean;
  handleRefreshTrips?: () => void;
}

export const TripList = ({
  futureTrips,
  pastTrips,
  isTimelineVisible,
  handleRefreshTrips
}: TripListProps) => {
  const isMobile = useIsMobile();
  const { trackTimelineAction, trackIndexAction } = useAnalytics();
  const location = useLocation();
  const [showAd, setShowAd] = useSpaSafeShowAd(location.pathname);
  

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (window && (window.adsbygoogle = window.adsbygoogle || [])) {
        try {
          (window.adsbygoogle = window.adsbygoogle || []).push({});
        } catch (e) {
          // console.error('[AdSenseWrapper] Error pushing ad:', e);
          // trackAdSenseAction("ad_failed", "failed", `slot: ${slot} - push error`);
        }
      }
    }, 2000); // 500ms delay
    return () => clearTimeout(timeout);
  } , [])

  // Track initial view of the trip list
  useEffect(() => {
    if (futureTrips.length > 0 || pastTrips.length > 0) {
      trackIndexAction('scroll_timeline', undefined, 
        `Viewing ${futureTrips.length} future and ${pastTrips.length} past trips`);
    }
  }, [futureTrips.length, pastTrips.length, trackIndexAction]);

  // Find the first ongoing trip (a trip with at least one segment that's currently ongoing)
  const findOngoingTrip = (trips: Trip[]): Trip | null => {
    try {
      const now = new Date();
      return trips.find(trip => {
        try {
          return trip.elements.some(element => {
            const startDate = parseISO(element.startDate || "1970");
            const endDate = parseISO(element.endDate || "1970");
            return !isAfter(startDate, now) && !isBefore(endDate, now);
          });
        } catch (error) {
          console.error('Error finding ongoing trip:', error);
          return false;
        }
      }) || null;
    } catch (error) {
      console.error('Error in findOngoingTrip:', error);
      return null;
    }
  };

  // Find trip to focus on (first ongoing trip, next upcoming trip, or most recent past trip)
  const findFocusedTrip = (): Trip | null => {
    try {
      // First check for ongoing trips
      const ongoingTrip = findOngoingTrip(futureTrips);
      if (ongoingTrip) return ongoingTrip;
      
      // If no ongoing trip, focus on the next upcoming trip (first in futureTrips)
      if (futureTrips.length > 0) return futureTrips[0];
      
      // If no future trips, focus on the most recent past trip (last in sortedPastTrips)
      if (pastTrips.length > 0) {
        // Sort past trips from oldest to newest
        const sortedPastTrips = [...pastTrips].sort((a, b) => {
          try {
            if (!a.elements?.length || !b.elements?.length) {
              return 0;
            }
            // Sort by the end date of the last trip element
            const aDate = a.elements.length > 0 ? parseISO(a.elements[a.elements.length - 1].endDate || "1970") : new Date(0);
            const bDate = b.elements.length > 0 ? parseISO(b.elements[b.elements.length - 1].endDate || "1970") : new Date(0);
            return compareAsc(aDate, bDate); // Oldest first (ascending)
          } catch (error) {
            console.error('Error sorting past trips for focus:', error);
            return 0;
          }
        });
        
        // Return the most recent past trip (last one in the sorted array)
        return sortedPastTrips[sortedPastTrips.length - 1];
      }
      
      // If no trips at all, return null
      return null;
    } catch (error) {
      console.error('Error finding focused trip:', error);
      return null;
    }
  };

  // Make sure pastTrips are sorted from oldest to newest (left to right)
  const sortedPastTrips = [...pastTrips].sort((a, b) => {
    try {
      if (!a.elements?.length || !b.elements?.length) {
        return 0; // If any trip has no elements, consider them equal
      }
      // Sort by the end date of the last trip element
      const aDate = a.elements.length > 0 ? parseISO(a.elements[a.elements.length - 1].endDate || "1970") : new Date(0);
      const bDate = b.elements.length > 0 ? parseISO(b.elements[b.elements.length - 1].endDate || "1970") : new Date(0);
      return compareAsc(aDate, bDate); // Oldest first (ascending)
    } catch (error) {
      console.error('Error sorting past trips:', error);
      return 0;
    }
  });

  // Make sure futureTrips are sorted from nearest to furthest in the future (left to right)
  const sortedFutureTrips = [...futureTrips].sort((a, b) => {
    try {
      if (!a.elements?.length || !b.elements?.length) {
        return 0; // If any trip has no elements, consider them equal
      }
      // Sort by the start date of the first trip element
      const aDate = a.elements.length > 0 ? parseISO(a.elements[0].startDate || "1970") : new Date();
      const bDate = b.elements.length > 0 ? parseISO(b.elements[0].startDate || "1970") : new Date();
      return compareAsc(aDate, bDate); // Nearest future first (ascending)
    } catch (error) {
      console.error('Error sorting future trips:', error);
      return 0;
    }
  });

  const focusedTrip = findFocusedTrip();
  
  // Track the focused trip
  useEffect(() => {
    // Removed direct AdSense constant logs to avoid reference errors after switching to getAdSenseConfig
    if (focusedTrip) {
      trackTimelineAction(
        'view_details', 
        'timeline', 
        focusedTrip.id, 
        focusedTrip.destination
      );
    }
  }, [focusedTrip, trackTimelineAction]);

 const { googleAds: googleAdsEnabled } = useFeatureFlagStatus(); // Use boolean flag
 
 const { publisherId } = getAdSenseConfig('728_90_Responsive');
  // For desktop view, use the grid layout component
  if (!isMobile) {
    return <DesktopTripList futureTrips={sortedFutureTrips} pastTrips={sortedPastTrips} handleRefreshTrips={handleRefreshTrips} />;
  }
  // For mobile view, render the timeline entries with ad after first trip
  const allTrips = [...sortedPastTrips, ...sortedFutureTrips];
  console.log('TripList: mobile view rendering', allTrips?.length);  
  const { slotId } = getAdSenseConfig(isMobile ? '350_250_Responsive' : '728_90_Responsive');
  const minHeight = isMobile ? '250px' : '90px';
  function shouldShowAd(idx: number, total: number) {
        if (total === 1) return idx === 0;
        if (total === 2) return idx === 0;
        if (total > 2) return idx === total - 2;
  return false;
}

  return (
    <>
      <GoogleAdsScriptLoader enabled={googleAdsEnabled} publisherId={publisherId} />
      <div style={{ zIndex: 0 }} className="px-4">
        <div className="flex flex-row items-stretch gap-6 h-full">
          {allTrips.map((trip, idx) => (
            <React.Fragment key={trip.id}>
              <TimelineEntry
                trip={trip}
                index={idx}
                isTimelineVisible={isTimelineVisible}
                isFocused={focusedTrip && trip.id === focusedTrip.id}
                handleRefreshTrips={handleRefreshTrips}
              />
              {/* Show AdSenseWrapper before the last TimelineEntry, as a true row item */}
                {(shouldShowAd(idx, allTrips.length)) &&
                googleAdsEnabled === true && showAd && (
                <div
                  style={{
                    width: '100%',
                    maxWidth: isMobile ? '100vw' : 350,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    margin: isMobile ? '32px 0 24px 0' : '0',
                    paddingTop: "12px",                 
                    alignItems: 'center',
                    alignSelf: 'stretch',
                    position: 'relative',
                    boxSizing: 'border-box',
                    height: 'auto',
                    background: 'transparent',                    
                    flex: isMobile ? '0 0 100vw' : '0 0 350px',
                    WebkitOverflowScrolling: isMobile ? 'touch' : undefined, // iOS momentum scroll fix
                  }}
                >
                  <AdSenseWrapper
                    style={{
                      width: '100%',   
                      paddingTop: "12px",             
                      background: '#fff',
                      boxSizing: 'border-box',
                      height: 'auto',
                      margin: '0 auto',                
                      WebkitOverflowScrolling: isMobile ? 'touch' : undefined, // iOS fix
                    }}
                    slot={slotId}
                    publisherId={publisherId}
                  />
                </div>
              )}
            </React.Fragment>
          ))}
          <TimelineDateIndicator />
        </div>
      </div>
    </>
  );
};
